import { useEffect, useRef, useState } from 'react'
import { Head, Link } from '@inertiajs/react'
import MarketingHeader from '@/components/MarketingHeader'
import PhoneMock from '@/components/PhoneMock'
import { money } from '@/lib/money'
import type { PricingProps } from '@/types'
import { t } from '@/lib/i18n'

// 落地页。移动优先（docs/designs/mobile/homepage.html），`md:` 把竖排摊成多列
// —— 稿子是移动端稿，但落地页有桌面访客，不能只做手机（S5 计划决定 C）。
//
// 四个锚点（#how / #features / #scenes / #mechanism）**不能改名**：
// MarketingHeader 的导航指着它们，而那个组件同时服务定价页。

const DETAIL_ICONS = { undo: '↩️', makeup: '🗓️', ledger: '🧾', share: '👀', pwa: '📱' } as const
const DETAIL_KEYS = [ 'undo', 'makeup', 'ledger', 'share', 'pwa' ] as const
const FAQ_KEYS = [ 'gaming', 'forgot', 'coparent', 'data' ] as const
const STEPS = [ 1, 2, 3, 4 ] as const

// 页脚链接：inline-flex + min-h-tap 让每条自己撑到 44px
const footerLink = 'inline-flex items-center min-h-tap px-1.5 text-muted hover:text-ink'

export default function Index({ pricing, android_beta }: {
  pricing: PricingProps
  /** Android 测试版。没配好安装包时是 null —— 那时整条入口不渲染。 */
  android_beta: { version: string } | null
}) {
  // 稿子的 `#sticky`：滑过首屏那组 CTA 之后，底部常驻一条。
  //
  // 用 IntersectionObserver 而不是 scroll 事件：这里要问的是一个布尔量
  // （首屏 CTA 还在视野里吗），而 scroll 是每帧一次回调。零依赖。
  //
  // ⚠️ 注意验证范围：IntersectionObserver 在**未渲染/隐藏**的页面里不回调，
  // 所以自动化里量不出「滑过之后它升起来」——那一半只能在真机或可见浏览器上看。
  // 渲染那一半（位置 / 高度 / 安全区 / 深色）是可以量的。
  const ctaRef = useRef<HTMLDivElement>(null)
  const [showSticky, setShowSticky] = useState(false)

  useEffect(() => {
    const el = ctaRef.current
    if (!el || typeof IntersectionObserver === 'undefined') return

    const io = new IntersectionObserver(
      ([ entry ]) => setShowSticky(!entry.isIntersecting),
      // 上边缘留 -80px：CTA 刚滑出去一点就出现，不用等它完全离开
      { rootMargin: '-80px 0px 0px 0px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  return (
    <div className="font-sans">
      <Head title={t('home.head_title')} />

      <MarketingHeader />

      {/* ============ 首屏 ============ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-glow" />
        <div className="relative max-w-[1080px] mx-auto px-5 pt-10 pb-12 md:pt-16 md:pb-20 grid md:grid-cols-2 gap-10 md:gap-12 items-center">
          <div>
            <span className="inline-block bg-tint text-brand font-bold px-3 py-1.5 rounded-full text-[13px] mb-4">
              🎁 {t('home.hero.badge')}
            </span>
            <h1 className="text-[32px] md:text-[52px] leading-[1.12] font-extrabold tracking-tight">
              {t('home.hero.title_a')}<br />
              <span className="grad-text">{t('home.hero.title_b')}</span>
            </h1>
            <p className="mt-4 md:mt-6 text-[16px] md:text-[17px] text-muted leading-relaxed max-w-[480px]">
              {t('home.hero.body')}
            </p>

            {/* 稿子的 `.cta`：移动端**整宽竖排进拇指区**，桌面端并排。
                原来是两个 inline 按钮 —— 在 375px 上它们并排挤成两行文字。 */}
            <div ref={ctaRef} className="mt-7 grid sm:flex gap-2.5">
              <Link
                href="/registration/new"
                className="min-h-[52px] flex items-center justify-center text-white grad-bg font-bold px-6 rounded-xl text-[16px] active:scale-[.985] hover:opacity-90 shadow-lg shadow-brand/25"
              >
                {t('home.hero.cta_primary')}
              </Link>
              {/* 稿子这里指向 kids-card.html —— 那一页没有路由（审计 §2.7 不排期），
                  所以改指四步那一段，不留一个指向 404 的链接。 */}
              <a
                href="#how"
                className="min-h-[52px] flex items-center justify-center bg-card border border-line font-bold px-6 rounded-xl text-[16px] hover:border-brand"
              >
                {t('home.hero.cta_secondary')}
              </a>
            </div>

            {/* Android 测试版入口。**只在服务端真的配好安装包时才出现**
                （android_beta 为 null 就整条不渲染）—— 落地页上挂一个点进去
                说「即将发布」的链接，比不挂更伤。

                指向 /download 而不是 APK 直链：那一页有「未知来源 + Play Protect」
                的安装引导，跳过它的话用户装到一半看到警告会以为中毒了。 */}
            {android_beta && (
              <a
                href="/download"
                className="mt-4 inline-flex items-center min-h-tap text-[13px] font-semibold text-brand hover:underline"
              >
                🤖 {t('home.android_beta', { version: android_beta.version })}
              </a>
            )}

            {/* 稿子的 `.trust`：三句短话。**每一句都在代码里成立** ——
                新手模板 / 多成员各一本账 / tasks#import 与剪贴板导出。 */}
            <ul className="mt-5 grid gap-1.5 text-[13px] text-muted list-none p-0">
              <li>✅ {t('home.trust.fast')}</li>
              <li>✅ {t('home.trust.multi_kid')}</li>
              <li>✅ {t('home.trust.export')}</li>
            </ul>
          </div>

          <PhoneMock />
        </div>
      </section>

      {/* ============ 怎么用？四步 ============ */}
      <section id="how" className="max-w-[1080px] mx-auto px-5 py-12 md:py-16">
        <h2 className="text-[24px] md:text-[32px] font-extrabold">{t('home.how.title')}</h2>
        <p className="mt-2 text-muted text-[15px] md:text-[16px]">{t('home.how.subtitle')}</p>

        {/* 稿子的 `.flow`：竖排，序号之间一条连线。
            原来是 md:grid-cols-4 横排 —— 在 375px 上四张卡叠成四屏高，
            而「四步」的重点是**顺序**，竖排 + 连线才把顺序画出来。 */}
        <ol className="mt-7 grid gap-0 md:grid-cols-2 md:gap-x-8 md:gap-y-6 list-none p-0">
          {STEPS.map((n, i) => (
            <li key={n} className="relative flex gap-3.5 pb-6 md:pb-0 last:pb-0">
              {/* 连线最后一步不画；桌面两列时也不画（顺序由左右决定） */}
              {i < STEPS.length - 1 && (
                <span aria-hidden="true" className="md:hidden absolute left-[15px] top-9 bottom-1 w-[2px] bg-line" />
              )}
              <span className="relative shrink-0 w-8 h-8 grid place-items-center rounded-full grad-bg text-white font-extrabold text-[14px]">
                {n}
              </span>
              <span className="min-w-0">
                <b className="block text-[16px]">{t(`home.how.step${n}.title`)}</b>
                <p className="m-0 mt-1 text-[14px] text-muted leading-relaxed">{t(`home.how.step${n}.body`)}</p>
              </span>
            </li>
          ))}
        </ol>
      </section>

      {/* ============ 谁在用 ============ */}
      <section id="scenes" className="max-w-[1080px] mx-auto px-5 pb-12 md:pb-16">
        <h2 className="text-[24px] md:text-[32px] font-extrabold">{t('home.who.title')}</h2>
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <div className="bg-card rounded-card shadow-card p-6">
            <div className="text-[28px]">🙋</div>
            <b className="block mt-2 text-[18px]">{t('home.who.self.title')}</b>
            <p className="m-0 mt-1.5 text-[14px] text-muted leading-relaxed">{t('home.who.self.body')}</p>
          </div>
          <div className="bg-card rounded-card shadow-card p-6">
            <div className="text-[28px]">👨‍👩‍👧‍👦</div>
            <b className="block mt-2 text-[18px]">{t('home.who.family.title')}</b>
            <p className="m-0 mt-1.5 text-[14px] text-muted leading-relaxed">{t('home.who.family.body')}</p>
          </div>
        </div>
      </section>

      {/* ============ 关键的几件小事 ============ */}
      <section id="features" className="bg-card border-y border-line">
        <div className="max-w-[1080px] mx-auto px-5 py-12 md:py-16">
          <h2 className="text-[24px] md:text-[32px] font-extrabold">{t('home.detail.title')}</h2>
          {/* 五条**差异化的具体保证**，不是六个 tab 的功能罗列 —— 后者任何同类
              产品都能写一份。每一条都在仓库里成立，逐条核对表见 S5 计划决定 B。 */}
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {DETAIL_KEYS.map((key) => (
              <div key={key} className="flex gap-3">
                <span aria-hidden="true" className="shrink-0 text-[22px]">{DETAIL_ICONS[key]}</span>
                <span className="min-w-0">
                  <b className="block text-[15px]">{t(`home.detail.${key}.title`)}</b>
                  <p className="m-0 mt-1 text-[14px] text-muted leading-relaxed">{t(`home.detail.${key}.body`)}</p>
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 每一分都有出处（上面那条保证的视觉证明）============ */}
      <section id="mechanism" className="max-w-[1080px] mx-auto px-5 py-12 md:py-16 grid md:grid-cols-2 gap-8 md:gap-12 items-center">
        <div>
          <span className="inline-block bg-tint text-brand font-bold px-3 py-1.5 rounded-full text-[13px] mb-3">
            🧾 {t('home.mechanism.badge')}
          </span>
          <h2 className="text-[22px] md:text-[30px] font-extrabold leading-tight">
            {t('home.mechanism.title_a')}<span className="grad-text">{t('home.mechanism.title_b')}</span>
          </h2>
          <p className="mt-3 text-muted text-[15px] md:text-[16px] leading-relaxed">{t('home.mechanism.body')}</p>
          <ul className="mt-4 grid gap-2 text-[14px] md:text-[15px] list-none p-0">
            <li className="flex gap-2"><span className="text-brand font-bold">✓</span> {t('home.mechanism.point1')}</li>
            <li className="flex gap-2"><span className="text-brand font-bold">✓</span> {t('home.mechanism.point2')}</li>
            <li className="flex gap-2"><span className="text-brand font-bold">✓</span> {t('home.mechanism.point3')}</li>
          </ul>
        </div>

        {/* 流水的样子。三行：加分 / 兑换 / 撤销 —— 撤销那行划掉且压暗，
            因为它说明的正是「撤销不删记录，反向记一笔」。 */}
        <div className="bg-sunk rounded-card p-4">
          <div className="text-[13px] text-muted font-semibold mb-2.5 px-1">📒 {t('ledger.title')}</div>
          <div className="grid gap-2">
            <div className="flex items-center gap-3 bg-card rounded-ctl px-3.5 py-2.5">
              <span className="text-base">🏃</span>
              <span className="min-w-0">
                <span className="block font-semibold text-[14px]">{t('home.demo.entry_earn')}</span>
                <span className="block text-[12px] text-faint">{t('home.demo.today_time')}</span>
              </span>
              <span className="flex-1" /><span className="text-green-strong font-bold">+10</span>
            </div>
            <div className="flex items-center gap-3 bg-card rounded-ctl px-3.5 py-2.5">
              <span className="text-base">🎮</span>
              <span className="min-w-0">
                <span className="block font-semibold text-[14px]">{t('home.demo.entry_redeem')}</span>
                <span className="block text-[12px] text-faint">{t('home.demo.yesterday_time_a')}</span>
              </span>
              <span className="flex-1" /><span className="text-red-strong font-bold">−50</span>
            </div>
            <div className="flex items-center gap-3 bg-card rounded-ctl px-3.5 py-2.5 opacity-60">
              <span className="text-base">↩️</span>
              <span className="min-w-0">
                <span className="block font-semibold text-[14px] line-through">{t('home.demo.entry_reversal')}</span>
                <span className="block text-[12px] text-faint">{t('home.demo.yesterday_time_b')}</span>
              </span>
              <span className="flex-1" /><span className="text-muted font-bold">−8</span>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 价格 ============ */}
      <section id="pricing" className="bg-card border-y border-line">
        <div className="max-w-[1080px] mx-auto px-5 py-12 md:py-16">
          <h2 className="text-[24px] md:text-[32px] font-extrabold">{t('home.price.title')}</h2>
          <p className="mt-2 text-muted text-[15px]">{t('home.price.subtitle')}</p>

          <div className="mt-6 max-w-[420px] bg-bg rounded-card shadow-card p-6">
            <div className="flex items-end gap-1">
              {/* 金额来自 props + 共享的 money()，**不写死** —— 稿子上那个
                  「¥18 / 月」与实际的 $4.99 不符（S5 计划决定 A）。 */}
              <span className="text-[38px] font-extrabold leading-none">
                {money(pricing.monthly_cents, pricing.currency)}
              </span>
              <span className="text-muted text-[15px] font-semibold">{t('pricing.per_month')}</span>
            </div>
            <p className="m-0 mt-1.5 text-[13px] text-muted">
              {t('home.price.yearly', {
                price: money(pricing.yearly_cents, pricing.currency),
                percent: pricing.yearly_save_percent,
              })}
            </p>

            <div className="mt-5 grid gap-2">
              <Link
                href="/registration/new"
                className="min-h-[52px] flex items-center justify-center text-white grad-bg font-bold rounded-xl text-[16px] active:scale-[.985]"
              >
                {t('home.price.cta')}
              </Link>
              {/* 完整档位表在 /pricing 那一页，这里不复述 —— 付费墙还没建，
                  把未强制的档位限制搬到第二个页面上只会把一个已知的债变成两处
                  （S5 计划决定 G）。 */}
              <Link
                href="/pricing"
                className="min-h-tap flex items-center justify-center text-brand font-semibold text-[14px]"
              >
                {t('home.price.compare')}
              </Link>
            </div>

            {/* 这一句是真的（app/views/legal/refund.*.erb §2），且复用定价页同一个 key */}
            <p className="m-0 mt-3 text-faint text-xs">{t('pricing.refund_note')}</p>
          </div>
        </div>
      </section>

      {/* ============ 常见问题 ============ */}
      <section className="max-w-[1080px] mx-auto px-5 py-12 md:py-16">
        <h2 className="text-[24px] md:text-[32px] font-extrabold">{t('home.faq.title')}</h2>
        {/* 原生 `<details>`：折叠状态由浏览器记，而且**不执行 JS 的抓取器也读得到
            答案** —— FAQ 是有 SEO 价值的内容，藏在 React state 后面等于对爬虫
            不存在。同 components/ui.tsx 的 Fold。 */}
        <div className="mt-5 grid gap-2 max-w-[720px]">
          {FAQ_KEYS.map((key) => (
            <details key={key} className="group bg-card rounded-card shadow-card px-4">
              <summary className="flex items-center gap-2 min-h-tap py-3 list-none cursor-pointer font-semibold text-[15px] [&::-webkit-details-marker]:hidden">
                {t(`home.faq.${key}.q`)}
                <span aria-hidden="true" className="ml-auto text-faint text-xs transition-transform group-open:rotate-180">▼</span>
              </summary>
              <p className="m-0 pb-4 text-[14px] text-muted leading-relaxed">{t(`home.faq.${key}.a`)}</p>
            </details>
          ))}
        </div>
      </section>

      {/* ============ 页脚 ============ */}
      <footer className="border-t border-line bg-card">
        {/* pb-32 给移动端那条常驻 CTA 让位，否则它压住最后一行 */}
        <div className="max-w-[1080px] mx-auto px-5 py-8 pb-32 md:pb-8 text-center">
          {/* 每条链接自己撑到 44px：并排的 21px 文字链在 375px 上点不准，
              而这几条是法务链接 —— 点错到隔壁那条比点不中更糟。 */}
          <div className="flex flex-wrap justify-center gap-x-3 text-[14px]">
            {/* 法务页是 ERB 服务端渲染的独立页面，用原生 a 而不是 Inertia Link */}
            <a href="/terms" className={footerLink}>{t('legal.terms')}</a>
            <a href="/privacy" className={footerLink}>{t('legal.privacy')}</a>
            <a href="/refund" className={footerLink}>{t('legal.refund')}</a>
            <Link href="/pricing" className={footerLink}>{t('home.nav.pricing')}</Link>
            <Link href="/session/new" className={footerLink}>{t('auth.login.title')}</Link>
          </div>
          <p className="m-0 mt-4 text-faint text-xs leading-relaxed">
            <span className="font-extrabold grad-text">📦 HabitChest</span>
            <br />{t('home.footer.tagline')}
          </p>
        </div>
      </footer>

      {/* 稿子的 `.stickyCta`。只在移动端：桌面顶栏一直贴在上面，
          里面已经有一个「免费开始」。`pb-safe` 垫 iPhone 的 Home 指示条。 */}
      <div
        className={
          'md:hidden fixed left-0 right-0 bottom-0 z-40 px-3 pt-3 pb-safe bg-card/95 backdrop-blur border-t border-line ' +
          'transition-transform duration-200 ' +
          (showSticky ? 'translate-y-0' : 'translate-y-full')
        }
      >
        <Link
          href="/registration/new"
          className="min-h-[52px] flex items-center justify-center text-white grad-bg font-bold rounded-xl text-[16px] active:scale-[.985]"
        >
          {t('home.sticky_cta')}
        </Link>
      </div>
    </div>
  )
}
